import { useEffect, useRef, useState } from 'react';

import {
  AdornedInput,
  Alert,
  Box,
  Button,
  ButtonGroup,
  FormField,
  FormHelp,
  Heading,
  Label,
  Modal,
  Select,
  Text,
  Toast,
  useToast,
} from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import {
  useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_numBathrooms,
  useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_numBedrooms,
  useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_squareFeet,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import { getSquareFeetError } from '../../../utils/formValidation';

import { bathOptions, bathOptionsArray, bedOptions, bedOptionsArray } from '../../../constants/ListingDetailsConstants';

import SkeletonPropertyDetailsModal from './components/SkeletonPropertyDetailsModal';

type Props = {
  isOpen: boolean;
  floorPlanId?: string;
  listingAlias?: string;
  isStreetEasyRestricted?: boolean;
  submitLabel?: string;
  initialBeds?: number | null;
  initialBaths?: number | null;
  onClose: () => void;
  onConfirm: () => void;
};

const bedSelectValues = new Set(bedOptionsArray.map(({ value }) => String(value)));
const bathSelectValues = new Set(bathOptionsArray.map(({ value }) => String(value)));

// Public property data can return counts with no matching option (14 beds, 2.75 baths) — leave those blank.
const toSelectValue = (value: number | null | undefined, selectValues: Set<string>) => {
  const selectValue = String(value ?? '');
  return selectValues.has(selectValue) ? selectValue : '';
};

const mutationMeta = {
  invalidates: ['listingDetails', 'getPropertyTableUnits', 'getPropertyTableUnitsList', 'getProperties'],
  context: { api: 'rental-manager', details: 'PropertyDetailsModal' },
};

const logPropertyDetailsError = (error: unknown, context: string) => {
  logError({
    error,
    errorType: 'PropertyDetailsModalError',
    context,
  });
};

const PropertyDetailsModal = ({
  isOpen,
  floorPlanId,
  listingAlias = '',
  isStreetEasyRestricted = false,
  submitLabel = 'Continue',
  initialBeds,
  initialBaths,
  onClose,
  onConfirm,
}: Props): JSX.Element => {
  const [beds, setBeds] = useState(() => toSelectValue(initialBeds, bedSelectValues));
  const [baths, setBaths] = useState(() => toSelectValue(initialBaths, bathSelectValues));
  const [size, setSize] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const hydratedFloorPlanKey = useRef('');

  const { data: listingDetails, isPending: isListingDetailsPending } = useGetListingDetails(listingAlias);

  const isEditMode = Boolean(listingAlias);
  const listingFloorPlan = listingDetails?.floorPlans?.[0];
  const resolvedFloorPlanId = floorPlanId ?? listingFloorPlan?.id;
  const isLoadingDetails = isEditMode && isListingDetailsPending;

  useEffect(() => {
    if (!listingAlias || !listingFloorPlan?.id) {
      return;
    }

    const floorPlanKey = `${listingAlias}:${listingFloorPlan.id}`;
    if (hydratedFloorPlanKey.current === floorPlanKey) {
      return;
    }

    setBeds(String(listingFloorPlan.numBedrooms ?? ''));
    setBaths(String(listingFloorPlan.numBathrooms ?? ''));
    setSize(String(listingFloorPlan.squareFeet ?? ''));
    hydratedFloorPlanKey.current = floorPlanKey;
  }, [listingAlias, listingFloorPlan]);

  const { enqueueToast } = useToast();

  const { mutateAsync: updateBedroomsAsync, status: bedsSaveState } =
    useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_numBedrooms({
      meta: mutationMeta,
      onError: (error) => logPropertyDetailsError(error, 'PropertyDetailsModal.updateBedrooms'),
    });
  const { mutateAsync: updateBathroomsAsync, status: bathsSaveState } =
    useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_numBathrooms({
      meta: mutationMeta,
      onError: (error) => logPropertyDetailsError(error, 'PropertyDetailsModal.updateBathrooms'),
    });
  const { mutateAsync: updateSquareFeetAsync, status: sizeSaveState } =
    useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_squareFeet({
      meta: mutationMeta,
      onError: (error) => logPropertyDetailsError(error, 'PropertyDetailsModal.updateSquareFeet'),
    });

  const showBedsError = hasSubmitted && !beds;
  const showBathsError = hasSubmitted && !baths;
  const sizeError = getSquareFeetError(size);
  const sizeValue = size === '' ? null : Number(size);
  const showLargeSizeWarning = !sizeError && sizeValue !== null && !Number.isNaN(sizeValue) && sizeValue >= 20000;
  const sizeFieldMessage =
    sizeError ??
    (showLargeSizeWarning
      ? 'Square footage values of 20,000 or more are unusually large. Verify this is correct.'
      : null);
  const isSaving = bedsSaveState === 'pending' || bathsSaveState === 'pending' || sizeSaveState === 'pending';

  const handleBedsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setBeds(e.target.value);
    setSubmitError(false);
  };

  const handleBathsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setBaths(e.target.value);
    setSubmitError(false);
  };

  const handleSubmit = async () => {
    setHasSubmitted(true);
    if (!beds || !baths || !resolvedFloorPlanId || sizeError) return;

    setSubmitError(false);
    try {
      const saves: Array<Promise<unknown>> = [
        updateBedroomsAsync({ postData: { floorPlanId: resolvedFloorPlanId, numBedrooms: Number(beds) } }),
        updateBathroomsAsync({ postData: { floorPlanId: resolvedFloorPlanId, numBathrooms: Number(baths) } }),
      ];
      if (size) {
        saves.push(updateSquareFeetAsync({ postData: { floorPlanId: resolvedFloorPlanId, squareFeet: Number(size) } }));
      }
      await Promise.all(saves);
      onConfirm();
    } catch {
      setSubmitError(true);
      enqueueToast(<Toast tone="critical">Changes couldn’t be saved. Try again.</Toast>);
    }
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'lg' }}
      header={<Heading level={2}>Tell us about your property</Heading>}
      body={
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
            <Text>
              {isStreetEasyRestricted
                ? 'Start collecting applications and managing tenants. You’ll continue to list on StreetEasy.'
                : 'Once you add your property, you can list it for free on Zillow, Trulia, and HotPads to help find your perfect renter.'}
            </Text>
          </Box>

          {isLoadingDetails ? (
            <SkeletonPropertyDetailsModal />
          ) : (
            <>
              {submitError && <Alert tone="critical">Changes could not be saved. Try again.</Alert>}

              <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
                <FormField
                  required
                  error={showBedsError}
                  label={<Label>Beds</Label>}
                  control={
                    <Select data-testid="beds-select" value={beds} onChange={handleBedsChange}>
                      {bedOptions.map((opt: string) => {
                        if (opt === 'studio') {
                          return (
                            <option key={opt} value={0}>
                              {opt}
                            </option>
                          );
                        }
                        return (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        );
                      })}
                    </Select>
                  }
                  description={showBedsError ? <FormHelp>Number of beds is required</FormHelp> : undefined}
                />

                <FormField
                  required
                  error={showBathsError}
                  label={<Label>Baths</Label>}
                  control={
                    <Select data-testid="baths-select" value={baths} onChange={handleBathsChange}>
                      {bathOptions.map((opt: string) => {
                        if (opt === '10+') {
                          return (
                            <option key={opt} value={10}>
                              {opt}
                            </option>
                          );
                        }
                        return (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        );
                      })}
                    </Select>
                  }
                  description={showBathsError ? <FormHelp>Number of bathrooms is required</FormHelp> : undefined}
                />

                <FormField
                  optional
                  error={Boolean(sizeFieldMessage)}
                  label={<Label>Size</Label>}
                  control={
                    <AdornedInput
                      input={
                        <AdornedInput.Input
                          data-testid="size-input"
                          type="text"
                          inputMode="numeric"
                          value={size}
                          onChange={(e) => {
                            setSize(e.target.value);
                            setSubmitError(false);
                          }}
                        />
                      }
                      endAdornment={<AdornedInput.Adornment>sqft</AdornedInput.Adornment>}
                    />
                  }
                  description={sizeFieldMessage ? <FormHelp>{sizeFieldMessage}</FormHelp> : undefined}
                />
              </Box>
            </>
          )}
        </Box>
      }
      footer={
        <ButtonGroup aria-label="Property details actions">
          <Button fluid={false} tone="brand" emphasis="bare" onClick={onClose}>
            Cancel
          </Button>
          <Button
            data-testid="property-details-continue-button"
            fluid={false}
            tone="brand"
            emphasis="filled"
            loading={isSaving}
            disabled={isLoadingDetails || Boolean(sizeError)}
            onClick={handleSubmit}
          >
            {submitLabel}
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default PropertyDetailsModal;
